import { appStore, authStore, Error, IMenuItem } from "src/framework/store";
import api, { IDataResponse, STATUS_CODE } from "../providers/api";
import jwt_decode from "jwt-decode";
import {
  EmptyResponseException,
  InvalidException,
  ParameterRequiredException,
} from "../exceptions/Error";
import { isLoggedIn$ } from "./LoginStore";

export interface UserInfo {
  user: string;
  name: string;
  email: string;
  permission: string[];
  groups: string[];
  menu: JwtMenu[];
}

export interface JwtUser {
  user: string;
  name: string;
  email: string;
}

export interface JwtMenu {
  nome: string;
  icone?: string;
  url?: string;
  menu?: JwtMenu[];
}

export interface JwtPermission {
  groups: string[];
  resources: string[];
  menu: JwtMenu[];
}

export interface JwtUserInfo {
  token: string;
  refreshToken: string;
  type: "user" | "other";
  user: JwtUser;
  permissions: JwtPermission;
  iat: number;
  exp: number;
}

class LoginStore1 {
  private token: string;
  private loggedIn: boolean;
  private userInfo: UserInfo | null;

  constructor() {
    this.token = "";
    this.userInfo = null;
    this.loggedIn = false;
    // makeAutoObservable(this);
  }

  public setLoggedIn(logged: boolean): void {
    this.loggedIn = logged;
  }

  public getLoggedIn(): boolean {
    return this.loggedIn;
  }

  public setUserInfo(userInfo: UserInfo | null): void {
    this.userInfo = userInfo;
    isLoggedIn$.next(true);
  }

  public getUserInfo(): UserInfo | null {
    return this.userInfo;
  }

  public getMenu(): IMenuItem[] {
    const menuCustom = this.getUserInfo()?.menu;

    if (menuCustom) {
      return menuCustom.map((rec: JwtMenu) => {
        const result: IMenuItem = {
          text: rec.nome,
          icon: (rec.icone || null),
          isSubItem: false,
          link: null,
          menuItem: rec.menu?.map((filho: JwtMenu) => {
            const result: IMenuItem = {
              text: filho.nome,
              icon: (filho.icone || null),
              isSubItem: true,
              link: (filho.url || null),
            }

            return result;
          })
        }

        return result;
      });
    }

    return [];
  }

  public setToken(token: string): void {
    this.token = token;
  }

  public getToken(): string {
    return this.token;
  }

  async doLogin(): Promise<void> {
    try {
      let credentials = authStore.getCredentials();

      if (credentials?.password === "" || credentials?.username === "") {
        throw new ParameterRequiredException(
          "Usuário ou senha estão em branco"
        );
      }
      // efetua o login na pla
      const response: IDataResponse = await api.post({
        url: "/login",
        data: credentials as object,
        headers: {
          Accpet: "*/*",
        },
      });

      if (response.statusCode === STATUS_CODE.BAD_REQUEST) {
        throw new InvalidException("Credenciais inválidas");
      }

      if (response.statusCode !== STATUS_CODE.OK) {
        if (response.statusCode === STATUS_CODE.INTERNAL_SERVER_ERROR) {
          throw new EmptyResponseException(
            "Erro ao tentar efetuar o login, serviço indisponível"
          );
        }
      }

      if (Object.keys(response.data).indexOf("token") < 0) {
        throw new EmptyResponseException(
          "Erro ao tentar efetuar o login, sem resposta do serviço"
        );
      }
      // decode jwt
      const jwtUserInfo: JwtUserInfo = jwt_decode<JwtUserInfo>(
        response.data.token
      );

      this.setToken(response.data.token);

      const userInfo: UserInfo = {
        email: jwtUserInfo?.user?.email,
        name: jwtUserInfo?.user?.name,
        user: jwtUserInfo?.user?.user,
        permission: jwtUserInfo?.permissions?.resources,
        groups: jwtUserInfo?.permissions?.groups,
        menu: jwtUserInfo?.permissions?.menu,
      };

      this.setUserInfo(userInfo);
      isLoggedIn$.next(true);
      this.saveLocalToken(response.data.token);
      this.saveLocalUserInfo(userInfo);
    } catch (error: any) {
      let _error: Error = {
        error: "",
        code: "",
        message: error["message"],
      };
      appStore.setError(_error.message);
    }
  }

  async doLogout(): Promise<void> {
    localStorage.removeItem("solar.token");
    localStorage.removeItem("solar.userInfo");
    this.setToken("");
    this.setUserInfo(null);
    this.setLoggedIn(false);
    isLoggedIn$.next(false);
  }

  saveLocalToken(token: string): void {
    localStorage.setItem("solar.token", token);
  }

  saveLocalUserInfo(userInfo: UserInfo): void {
    localStorage.setItem("solar.userInfo", JSON.stringify(userInfo));
  }

  loadLocalToken(): string {
    let token: string | null = localStorage.getItem("solar.token");
    if (token === null) {
      return "";
    }
    return token;
  }

  validateLogin(): void {
    try {
      const token: string = this.loadLocalToken();

      // se encontar o token e ainda for valido faz o login persistido
      if (token) {
        // decode jwt
        const jwtUserInfo: JwtUserInfo = jwt_decode<JwtUserInfo>(token);
        const userInfo: UserInfo = {
          email: jwtUserInfo?.user?.email,
          name: jwtUserInfo?.user?.name,
          user: jwtUserInfo?.user?.user,
          permission: jwtUserInfo?.permissions?.resources,
          groups: jwtUserInfo?.permissions?.groups,
          menu: jwtUserInfo?.permissions?.menu,
        };
        this.setUserInfo(userInfo);
        this.setToken(token);
      }
    } catch (error) {
      localStorage.removeItem("solar.auth");
      localStorage.removeItem("solar.userInfo");
    }
  }

  isUserPermission(name: string, permissions?: string[]): boolean {
    console.log("Validando permissão", name);


    if (permissions && permissions.length) {
      const token: string = this.loadLocalToken();
      if (token) {
        const jwtUserInfo: JwtUserInfo = jwt_decode<JwtUserInfo>(token);

        if (jwtUserInfo.permissions.resources[0] === "*") {
          return true;
        }

        return jwtUserInfo.permissions.resources.includes(name);
      }
    } else {
      if (permissions && permissions[0] === "*") {
        return true;
      }

      return (permissions || []).includes(name);
    }

    return false;
  }

  getPermissions(): string[] {
    console.log("Recuperou as permissões");

    const token: string = this.loadLocalToken();

    if (token) {
      const jwtUserInfo: JwtUserInfo = jwt_decode<JwtUserInfo>(token);
      return jwtUserInfo.permissions.resources;
    }

    return [];
  }

  isSuperUser(): boolean {
    if (this.userInfo?.groups?.includes("Administrador") || this.userInfo?.permission?.includes("*")) {
      return true;
    }

    return false;
  }
}

const loginStore = new LoginStore1();

export { loginStore };
